import { create } from 'zustand';
import type { Camera, CameraState, SessionState } from '../types';
import { SessionDisplay, toSessionDisplays } from '../models/camera';

/**
 * Camera Store Interface
 */
interface CameraStore {
  // ========================================
  // State
  // ========================================

  /** Backend raw data */
  cameras: Camera[];

  /** Frontend view models (computed from cameras) */
  SessionDisplays: SessionDisplay[];

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: string | null;

  /** Modal states */
  settingsModalOpen: boolean;
  stopModalOpen: boolean;
  stopAllSessionsModalOpen: boolean;
  selectedCameraId: string | null;

  // ========================================
  // Actions
  // ========================================

  /** Set all cameras (replaces existing) */
  setCameras: (cameras: Camera[]) => void;

  /** Update a single camera by id */
  updateCamera: (id: string, updates: Partial<Camera>) => void;

  /** Update camera state only */
  updateCameraState: (id: string, cameraState: CameraState) => void;

  /** Update session state only */
  updateSessionState: (id: string, sessionState: SessionState) => void;

  /** Update both camera and session states */
  updateStates: (
    id: string,
    cameraState: CameraState,
    sessionState: SessionState
  ) => void;

  /** Add a new camera */
  addCamera: (camera: Camera) => void;

  /** Remove a camera by id */
  removeCamera: (id: string) => void;

  /** Set loading state */
  setLoading: (isLoading: boolean) => void;

  /** Set error state */
  setError: (error: string | null) => void;

  /** Clear all data */
  clear: () => void;

  // ========================================
  // Modal Actions
  // ========================================

  /** Open settings modal */
  openSettingsModal: (cameraId: string) => void;

  /** Close settings modal */
  closeSettingsModal: () => void;

  /** Open stop modal */
  openStopModal: (cameraId: string) => void;

  /** Close stop modal */
  closeStopModal: () => void;

  /** Open stop all sessions modal */
  openStopAllSessionsModal: () => void;

  /** Close stop all sessions modal */
  closeStopAllSessionsModal: () => void;

  // ========================================
  // Selectors
  // ========================================

  /** Get camera view model by id */
  getCameraById: (id: string) => SessionDisplay | undefined;

  /** Get all connected cameras (connected or playing) */
  getConnectedCameras: () => SessionDisplay[];

  /** Get all streaming cameras (playing) */
  getStreamingCameras: () => SessionDisplay[];

  /** Get all counting cameras */
  getCountingCameras: () => SessionDisplay[];

  /** Get cameras that can start session */
  getReadyCameras: () => SessionDisplay[];

  /** Get cameras in error state */
  getErrorCameras: () => SessionDisplay[];

  /** Check if all cameras can start sessions */
  canStartAllSessions: () => boolean;

  /** Get total count across all cameras */
  getTotalCount: () => number;

  /** Get total weight across all cameras */
  getTotalWeight: () => number;
}

/**
 * Camera Store
 * Manages camera state with Zustand
 */
export const useCameraStore = create<CameraStore>((set, get) => ({
  // ========================================
  // Initial State
  // ========================================
  cameras: [],
  SessionDisplays: [],
  isLoading: false,
  error: null,
  settingsModalOpen: false,
  stopModalOpen: false,
  stopAllSessionsModalOpen: false,
  selectedCameraId: null,

  // ========================================
  // Actions
  // ========================================

  setCameras: (cameras) => {
    const currentCameras = get().cameras;

    // Skip update if cameras array hasn't changed (deep comparison)
    if (
      currentCameras.length === cameras.length &&
      currentCameras.every((curr, i) => {
        const newCam = cameras[i];
        return (
          curr.id === newCam.id &&
          curr.name === newCam.name &&
          curr.cameraState === newCam.cameraState &&
          curr.sessionState === newCam.sessionState &&
          curr.count === newCam.count &&
          curr.weight === newCam.weight &&
          curr.weightPerFish === newCam.weightPerFish
        );
      })
    ) {
      return; // No changes, skip update
    }

    const SessionDisplays = toSessionDisplays(cameras);
    set({ cameras, SessionDisplays, error: null });
  },

  updateCamera: (id, updates) => {
    set((state) => {
      const cameras = state.cameras.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      );
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  updateCameraState: (id, cameraState) => {
    set((state) => {
      const cameras = state.cameras.map((c) =>
        c.id === id ? { ...c, cameraState } : c
      );
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  updateSessionState: (id, sessionState) => {
    set((state) => {
      const cameras = state.cameras.map((c) =>
        c.id === id ? { ...c, sessionState } : c
      );
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  updateStates: (id, cameraState, sessionState) => {
    set((state) => {
      const cameras = state.cameras.map((c) =>
        c.id === id ? { ...c, cameraState, sessionState } : c
      );
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  addCamera: (camera) => {
    set((state) => {
      const cameras = [...state.cameras, camera];
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  removeCamera: (id) => {
    set((state) => {
      const cameras = state.cameras.filter((c) => c.id !== id);
      const SessionDisplays = toSessionDisplays(cameras);
      return { cameras, SessionDisplays };
    });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  clear: () => {
    set({
      cameras: [],
      SessionDisplays: [],
      isLoading: false,
      error: null,
      settingsModalOpen: false,
      stopModalOpen: false,
      stopAllSessionsModalOpen: false,
      selectedCameraId: null,
    });
  },

  // ========================================
  // Modal Actions
  // ========================================

  openSettingsModal: (cameraId) => {
    set({ settingsModalOpen: true, selectedCameraId: cameraId });
  },

  closeSettingsModal: () => {
    set({ settingsModalOpen: false, selectedCameraId: null });
  },

  openStopModal: (cameraId) => {
    set({ stopModalOpen: true, selectedCameraId: cameraId });
  },

  closeStopModal: () => {
    set({ stopModalOpen: false, selectedCameraId: null });
  },

  openStopAllSessionsModal: () => {
    set({ stopAllSessionsModalOpen: true });
  },

  closeStopAllSessionsModal: () => {
    set({ stopAllSessionsModalOpen: false });
  },

  // ========================================
  // Selectors
  // ========================================

  getCameraById: (id) => {
    return get().SessionDisplays.find((c) => c.id === id);
  },

  getConnectedCameras: () => {
    return get().SessionDisplays.filter((c) => c.isConnected);
  },

  getStreamingCameras: () => {
    return get().SessionDisplays.filter((c) => c.isStreaming);
  },

  getCountingCameras: () => {
    return get().SessionDisplays.filter((c) => c.isCounting);
  },

  getReadyCameras: () => {
    return get().SessionDisplays.filter((c) => c.canStartSession);
  },

  getErrorCameras: () => {
    return get().SessionDisplays.filter((c) => c.hasError);
  },

  canStartAllSessions: () => {
    const SessionDisplays = get().SessionDisplays;
    if (SessionDisplays.length === 0) return false;
    // Check if all cameras can start AND no camera is in transition state
    return SessionDisplays.every((c) => c.canStartSession && !c.isTransitioning);
  },

  getTotalCount: () => {
    return get().SessionDisplays.reduce((sum, c) => sum + c.count, 0);
  },

  getTotalWeight: () => {
    return get().SessionDisplays.reduce((sum, c) => sum + c.weight, 0);
  },
}));

/**
 * Selector Hooks for convenience
 * These can be used directly without calling store methods
 */

/** Get camera by id */
export const useCameraById = (id: string) =>
  useCameraStore((state) => state.getCameraById(id));

/** Get all connected cameras */
export const useConnectedCameras = () =>
  useCameraStore((state) => state.getConnectedCameras());

/** Get all streaming cameras */
export const useStreamingCameras = () =>
  useCameraStore((state) => state.getStreamingCameras());

/** Get all counting cameras */
export const useCountingCameras = () =>
  useCameraStore((state) => state.getCountingCameras());

/** Get cameras ready to start session */
export const useReadyCameras = () =>
  useCameraStore((state) => state.getReadyCameras());

/** Get cameras in error state */
export const useErrorCameras = () =>
  useCameraStore((state) => state.getErrorCameras());

/** Check if all cameras can start sessions */
export const useCanStartAllSessions = () =>
  useCameraStore((state) => state.canStartAllSessions());

/** Get total count */
export const useTotalCount = () =>
  useCameraStore((state) => state.getTotalCount());

/** Get total weight */
export const useTotalWeight = () =>
  useCameraStore((state) => state.getTotalWeight());

/** Get all view models */
export const useCameraSessionDisplays = () =>
  useCameraStore((state) => state.SessionDisplays);

/** Get loading state */
export const useIsLoadingCameras = () =>
  useCameraStore((state) => state.isLoading);

/** Get error state */
export const useCameraError = () =>
  useCameraStore((state) => state.error);
